// src/pages/users/UserLegalField.tsx
//
// The "Legal" column of the admin user list: where a user stands with the
// legal documents and which ones they still have to accept.
import { Box, Chip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRecordContext } from 'react-admin';
import { readPublishedLegal } from '../../legal/legalConfig';
import type { LegalState } from '../../legal/legalState';
import { userLegalStatus, type UserLegalState } from './userLegalStatus';

const COLOR: Record<UserLegalState, 'success' | 'info' | 'warning' | 'error'> =
    {
        ok: 'success',
        announced: 'info',
        due: 'warning',
        blocked: 'error',
        // The server refuses a user without the record, like a blocked one.
        unset: 'error',
    };

// One read of config/legal for the whole list, not one per row.
const usePublishedLegal = () =>
    useQuery({
        queryKey: ['legal', 'published'],
        queryFn: readPublishedLegal,
        staleTime: 5 * 60 * 1000,
    });

// The datagrid reads the column title from `label`, as for the other
// columns of the list.
interface UserLegalFieldProps {
    label?: string | boolean;
}

export const UserLegalField = (_props: UserLegalFieldProps) => {
    const record = useRecordContext();
    const { data: published } = usePublishedLegal();
    if (!record) return null;

    // Without the published documents the state is still known; only the
    // names of what is outstanding are not.
    const status = userLegalStatus(
        record.legal as LegalState | undefined,
        published?.documents,
        Date.now()
    );
    return (
        <Box>
            <Chip
                label={status.label}
                size="small"
                variant="outlined"
                color={COLOR[status.state]}
            />
            {/* The date in the cell itself: someone scanning the list should
                not have to hover over every row for it. */}
            {status.detail && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    {status.detail}
                </Typography>
            )}
            {status.documents.length > 0 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                >
                    {status.documents.join(', ')}
                </Typography>
            )}
        </Box>
    );
};
